import type React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/atoms/Buttons';
import { readingLabelKey } from '@/components/molecules/BioscopeSystemReadings/readingLabels';
import { EmptyState } from '@/components/molecules/EmptyState';
import { KeyValueTable } from '@/components/organisms/DataTable';
import { formatDateTimeWorded } from '@/utils/date';

import styles from './BioscopeConfiguration.module.scss';

export interface BioscopeConfigurationProps {
    children?: React.ReactNode;
    bioscope?: { id: number }; // Assuming bioscope prop contains an id
    configData: Record<string, unknown> | null;
    handlePublish: (topic: string, message: string) => void;
}

function configText(key: string, value: unknown): React.ReactNode {
    if (key === 'timestamp') {
        return typeof value === 'number' || typeof value === 'string'
            ? formatDateTimeWorded(value)
            : '';
    }
    if (typeof value === 'boolean') return value.toString();
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'string' || typeof value === 'number') return value;
    return null;
}

function BioscopeConfiguration({
    children,
    bioscope,
    configData,
    handlePublish,
}: BioscopeConfigurationProps) {
    const { t } = useTranslation();
    const configLabel = (key: string) => {
        const labelKey = readingLabelKey(key);
        return labelKey ? t(labelKey) : key;
    };

    const handleRefresh = () => {
        const topic = `bioscope/config/${bioscope?.id}`;
        const message = 'fetch_bioscope_config';
        handlePublish(topic, message);
    };
    return (
        <div className={styles.BioscopeConfiguration}>
            <h2>{t('bioscopeDetail.configuration.title')}</h2>
            {children}
            <div className={styles.section}>
                <Button variant="secondary" type="button" onClick={handleRefresh}>
                    {t('bioscopeDetail.configuration.refresh')}
                </Button>
            </div>
            <div className={styles.section}>
                {configData ? (
                    <KeyValueTable
                        tableClassName={styles.nowrapTable}
                        rows={Object.keys(configData).map((key) => ({
                            key,
                            label: <strong>{configLabel(key)}</strong>,
                            value: configText(key, configData[key]),
                        }))}
                    />
                ) : (
                    <EmptyState title={t('common.labels.noData')} />
                )}
            </div>
        </div>
    );
}

export default BioscopeConfiguration;
export { BioscopeConfiguration };
